import { useEffect, useLayoutEffect, useRef, useState } from "react";
import { Animated, Easing, Modal, Platform, StyleSheet, Text } from "react-native";

import { fonts, palette } from "@/design/tokens";

const brandMark = require("../../public/brand/icon.png");

/**
 * Full-screen brand veil. Rendered as an absolute overlay ABOVE the app so
 * screens mount, lay out, and decode their artwork underneath it; when
 * `visible` flips false it fades away to reveal a fully composed page —
 * nothing may jitter in after the reveal.
 *
 * The mark itself is the loading indicator: it breathes. The wordmark sits
 * steady beneath it — fonts are loaded before this tree renders, so it can
 * never arrive late inside the curtain.
 *
 * The breath is a symmetric pendulum: equal legs and the same CSS ease-in-out
 * curve as the Cast receiver, with no dwell at either extreme.
 */

const useNative = Platform.OS !== "web";
const breathe = Easing.bezier(0.42, 0, 0.58, 1);
const BREATH_LEG_MS = 800;

export function BrandCurtain({
  visible = true,
  nativeModal = false,
  revealMark = false
}: {
  visible?: boolean;
  /** Place the root gate above Expo Router's native navigation window. */
  nativeModal?: boolean;
  /** Fade the brand lockup in over the already-opaque launch surface. */
  revealMark?: boolean;
}) {
  const opacity = useRef(new Animated.Value(visible ? 1 : 0)).current;
  const breath = useRef(new Animated.Value(1)).current;
  const markReveal = useRef(new Animated.Value(0)).current;
  const [mounted, setMounted] = useState(visible);
  const [brandReady, setBrandReady] = useState(false);

  useEffect(() => {
    markReveal.stopAnimation();
    if (!mounted || !brandReady) {
      markReveal.setValue(0);
      return undefined;
    }

    markReveal.setValue(0);
    const animation = Animated.timing(markReveal, {
      toValue: 1,
      duration: revealMark ? 460 : 180,
      easing: Easing.bezier(0.16, 1, 0.3, 1),
      useNativeDriver: useNative
    });
    animation.start();
    return () => animation.stop();
  }, [brandReady, markReveal, mounted, revealMark]);

  useEffect(() => {
    if (!mounted) {
      breath.setValue(1);
      return undefined;
    }

    const loop = Animated.loop(
      Animated.sequence([
        Animated.timing(breath, {
          toValue: 0,
          duration: BREATH_LEG_MS,
          easing: breathe,
          useNativeDriver: useNative
        }),
        Animated.timing(breath, {
          toValue: 1,
          duration: BREATH_LEG_MS,
          easing: breathe,
          useNativeDriver: useNative
        })
      ])
    );
    loop.start();
    return () => loop.stop();
  }, [breath, mounted]);

  useLayoutEffect(() => {
    if (visible) {
      setMounted(true);
      // Re-arming during sign-in must cover the authenticated shell in the
      // same paint. Fading the curtain in exposes exactly the empty frame the
      // curtain exists to prevent.
      opacity.stopAnimation();
      opacity.setValue(1);
      return;
    }
    Animated.timing(opacity, {
      toValue: 0,
      duration: 340,
      easing: Easing.bezier(0.22, 1, 0.36, 1),
      useNativeDriver: useNative
    }).start(({ finished }) => {
      if (finished) setMounted(false);
    });
  }, [opacity, visible]);

  // `visible` may turn true while the curtain is unmounted. Render it in that
  // same commit; the layout effect above makes it fully opaque before paint.
  if (!mounted && !visible) return null;

  const curtain = (
    <Animated.View
      style={[
        styles.overlay,
        nativeModal && useNative ? styles.modalOverlay : styles.absoluteOverlay,
        { opacity, pointerEvents: visible ? "auto" : "none" }
      ]}
    >
      <Animated.View
        style={[
          styles.brandLockup,
          {
            opacity: markReveal,
            transform: [
              {
                scale: markReveal.interpolate({
                  inputRange: [0, 1],
                  outputRange: [0.985, 1]
                })
              }
            ]
          }
        ]}
      >
        <Animated.Image
          source={brandMark}
          accessibilityLabel="Recital Master"
          onError={() => setBrandReady(true)}
          onLoad={() => setBrandReady(true)}
          style={[
            styles.icon,
            {
              opacity: breath.interpolate({ inputRange: [0, 1], outputRange: [0.72, 1] }),
              transform: [
                {
                  scale: breath.interpolate({
                    inputRange: [0, 1],
                    outputRange: [0.965, 1.03]
                  })
                }
              ]
            }
          ]}
        />
        <Text style={styles.wordmark}>Recital Master</Text>
      </Animated.View>
    </Animated.View>
  );

  // Expo Router's native Stack is backed by react-native-screens. An
  // absolutely-positioned sibling cannot reliably cover that native window,
  // which produced the visible header and bottom-positioned curtain mark on
  // Android. Modal gives the root gate a true full-window layer above it.
  if (nativeModal && useNative) {
    return (
      <Modal
        animationType="none"
        hardwareAccelerated
        navigationBarTranslucent
        onRequestClose={() => {}}
        presentationStyle="overFullScreen"
        statusBarTranslucent
        transparent
        visible={mounted || visible}
      >
        {curtain}
      </Modal>
    );
  }

  return curtain;
}

const styles = StyleSheet.create({
  absoluteOverlay: {
    ...StyleSheet.absoluteFillObject
  },
  modalOverlay: {
    flex: 1
  },
  overlay: {
    alignItems: "center",
    backgroundColor: palette.bg,
    justifyContent: "center",
    zIndex: 100
  },
  brandLockup: {
    alignItems: "center"
  },
  icon: {
    height: 80,
    width: 80
  },
  // Login's wordmark, held steady while the mark above it breathes.
  wordmark: {
    color: palette.textSoft,
    fontFamily: fonts.displaySemi,
    fontSize: 17,
    marginTop: 14,
    textAlign: "center"
  }
});
